import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  employeesGetAll,
  locationSalesGetSalesByEmployee,
  type SalesByEmployeeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Combobox } from "~/components/ui/combobox";
import { toConvexTableId } from "~/lib/convex-table-id";
import { optionalSearchString } from "~/lib/parse-enum-value";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute(
  "/_authenticated/reports/employee-sales-summary"
)({
  component: EmployeeSalesSummaryPage,
  validateSearch: (search: Record<string, unknown>) => ({
    location: optionalSearchString(search.location),
    employeeId: optionalSearchString(search.employeeId),
  }),
});

type StaffSalesData = SalesByEmployeeEntry;

function EmployeeSalesSummaryPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const employeeId = searchParams.employeeId;

  const { data: employeesResult } = useQuery(
    convexQuery(employeesGetAll, {
      paginationOpts: { numItems: 500, cursor: null },
    })
  );
  const employees =
    employeesResult?.page.map((e) => ({
      id: e.id,
      firstName: e.firstName,
      lastName: e.lastName,
    })) ?? [];

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Employee
            Sales Summary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">
          Employee Sales Summary
        </h2>
        <p className="body text-muted-foreground">
          View detailed employee sales performance and statistics
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Combobox
          className="bg-background h-9 w-48 min-w-0 rounded-md p-2"
          dropdownClassName="w-48"
          items={employees.map((e) => ({
            label: `${e.firstName} ${e.lastName}`,
            value: e.id,
            keywords: [e.firstName, e.lastName],
          }))}
          onValueChange={(v) => {
            void navigate({
              from: "/reports/employee-sales-summary",
              search: (prev) => ({
                ...prev,
                employeeId: v ?? undefined,
              }),
            });
          }}
          placeholder="Select employee (optional)..."
          value={employeeId ?? null}
        />
      </div>

      <EmployeeSalesSummaryContent
        employeeId={employeeId}
        locationId={locationId}
      />
    </div>
  );
}

const columnHelper = createColumnHelper<StaffSalesData>();

const EmployeeSalesSummaryContent = ({
  locationId,
  employeeId,
}: {
  locationId: string;
  employeeId?: string | null;
}) => {
  const [dateRange] = useSearchParamsDateRange();

  const { data, isPending: isLoading } = useQuery(
    convexQuery(locationSalesGetSalesByEmployee, {
      dateRange: {
        from: dateRange.from?.getTime() ?? 0,
        to: dateRange.to?.getTime() ?? 0,
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  // Filter by employee if one is selected
  const filteredData = useMemo(() => {
    if (!data) {
      return [];
    }
    if (!employeeId) {
      return data;
    }
    return data.filter((item) => item.employee.id === employeeId);
  }, [data, employeeId]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("employee", {
          header: "Employee",
          cell: ({ getValue }) => {
            const employee = getValue();
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.employee.id),
          meta: {
            getCellForExport: (e: StaffSalesData["employee"]) =>
              `${e.firstName} ${e.lastName}`,
            faceting: {
              title: "Employee",
              getOption: (e: StaffSalesData["employee"]) => ({
                value: e.id,
                label: `${e.firstName} ${e.lastName}`,
              }),
            },
          },
        }),
        columnHelper.accessor("billsClosed", {
          header: "Bills Closed",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: StaffSalesData["billsClosed"]) => d,
          },
        }),
        columnHelper.accessor("hoursWorked", {
          header: "Hours Worked",
          cell: ({ getValue }) => getValue()?.toFixed(1),
          meta: {
            getCellForExport: (d: StaffSalesData["hoursWorked"]) => d,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: StaffSalesData["amountSold"]) => d,
          },
        }),
        columnHelper.accessor("shareOfTotalSales", {
          header: "Sales %",
          cell: ({ getValue }) => `${(getValue() * 100)?.toFixed(1)}%`,
          meta: {
            getCellForExport: (d: StaffSalesData["shareOfTotalSales"]) =>
              d * 100,
          },
        }),
        columnHelper.display({
          id: "avgSalesPerHour",
          header: "Avg Sales/Hour",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked;
            const amountSold = row.original.amountSold;
            if (!hoursWorked || hoursWorked === 0) {
              return "$0.00";
            }
            const avgPerHour = amountSold / hoursWorked;
            return avgPerHour.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: StaffSalesData) => {
              const hoursWorked = row.hoursWorked;
              const amountSold = row.amountSold;
              if (!hoursWorked || hoursWorked === 0) {
                return 0;
              }
              return amountSold / hoursWorked;
            },
          },
        }),
        columnHelper.display({
          id: "avgSalesPerBill",
          header: "Avg Sales/Bill",
          cell: ({ row }) => {
            const billsClosed = row.original.billsClosed;
            const amountSold = row.original.amountSold;
            if (!billsClosed || billsClosed === 0) {
              return "$0.00";
            }
            const avgPerBill = amountSold / billsClosed;
            return avgPerBill.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: StaffSalesData) => {
              const billsClosed = row.billsClosed;
              const amountSold = row.amountSold;
              if (!billsClosed || billsClosed === 0) {
                return 0;
              }
              return amountSold / billsClosed;
            },
          },
        }),
      ] as ColumnDef<StaffSalesData>[],
    []
  );

  if (isLoading) {
    return <LoadingPanel message="Loading employee sales data..." />;
  }

  return (
    <SalesDataTable
      columns={columns}
      data={filteredData}
      id="employee-sales-summary-table"
    />
  );
};
